type ChangeListener = () => void;

type ObservablePlainObject = Record<string, unknown>;

type DebugPathParent = {
  parent: ObservableModelNode;
  key: string;
  container?: unknown[];
};

const ARRAY_MUTATION_METHODS = [
  "copyWithin",
  "fill",
  "pop",
  "push",
  "reverse",
  "shift",
  "sort",
  "splice",
  "unshift",
] as const;

function isPlainObject(value: unknown): value is ObservablePlainObject {
  if (!value || typeof value !== "object") {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export class ObservableModelNode {
  private observerRoot: ObservableModelRoot | null = null;
  private debugPathParent: DebugPathParent | null = null;
  private debugPathRoot: string | null = null;
  private observedObjectKeys = new WeakMap<object, Set<string>>();
  private observedArrays = new WeakSet<object>();
  private observableKeys = new Set<string>();

  get _debugPath(): string | null {
    if (this.debugPathRoot) {
      return this.debugPathRoot;
    }

    if (!this.debugPathParent) {
      return null;
    }

    const parentPath: string | null = this.debugPathParent.parent._debugPath;
    if (!parentPath) {
      return null;
    }

    if (this.debugPathParent.container) {
      const index = this.debugPathParent.container.indexOf(this);
      return index === -1
        ? null
        : `${parentPath}.${this.debugPathParent.key}[${index}]`;
    }

    return `${parentPath}.${this.debugPathParent.key}`;
  }

  protected defineObservableValue<T>(key: string, initialValue: T) {
    this.observableKeys.add(key);
    let value = this.prepareObservedValue(initialValue, { key });

    Object.defineProperty(this, key, {
      configurable: true,
      enumerable: true,
      get: () => value,
      set: (nextValue: T) => {
        if (Object.is(value, nextValue)) {
          return;
        }

        value = this.prepareObservedValue(nextValue, { key });
        this.markChanged();
      },
    });
  }

  setObserverRoot(root: ObservableModelRoot | null) {
    this.observerRoot = root;
    this.syncChildRoots();
    return this;
  }

  setDebugPathRoot(debugPathRoot: string | null) {
    this.debugPathRoot = debugPathRoot;
    this.syncChildDebugPaths();
    return this;
  }

  private setDebugPathParent(debugPathParent: DebugPathParent | null) {
    this.debugPathRoot = null;
    this.debugPathParent = debugPathParent;
    this.syncChildDebugPaths();
    return this;
  }

  protected markChanged() {
    this.observerRoot?.markChanged();
  }

  private prepareObservedValue<T>(
    value: T,
    debugPathParent?: Omit<DebugPathParent, "parent">
  ): T {
    if (value instanceof ObservableModelNode) {
      value.setObserverRoot(this.observerRoot);
      if (debugPathParent) {
        value.setDebugPathParent({
          parent: this,
          ...debugPathParent,
        });
      }
      return value;
    }

    if (Array.isArray(value)) {
      return this.observeArray(value, debugPathParent?.key) as T;
    }

    if (isPlainObject(value)) {
      return this.observePlainObject(value) as T;
    }

    return value;
  }

  private observeArray<T>(value: T[], key?: string): T[] {
    if (this.observedArrays.has(value)) {
      value.forEach((entry) => {
        this.prepareObservedValue(
          entry,
          this.getArrayItemDebugParent(value, key)
        );
      });
      return value;
    }

    this.observedArrays.add(value);

    for (const methodName of ARRAY_MUTATION_METHODS) {
      const original = value[methodName] as (...args: unknown[]) => unknown;

      Object.defineProperty(value, methodName, {
        configurable: true,
        writable: true,
        enumerable: false,
        value: (...args: unknown[]) => {
          const result = original.apply(value, args);
          value.forEach((entry) => {
            this.prepareObservedValue(
              entry,
              this.getArrayItemDebugParent(value, key)
            );
          });
          this.markChanged();
          return result;
        },
      });
    }

    value.forEach((entry) => {
      this.prepareObservedValue(
        entry,
        this.getArrayItemDebugParent(value, key)
      );
    });

    return value;
  }

  private getArrayItemDebugParent<T>(
    container: T[],
    key?: string
  ): Omit<DebugPathParent, "parent"> | undefined {
    return key ? { key, container } : undefined;
  }

  private observePlainObject<T extends ObservablePlainObject>(value: T): T {
    const observedKeys =
      this.observedObjectKeys.get(value) ?? new Set<string>();

    this.observedObjectKeys.set(value, observedKeys);

    for (const key of Object.keys(value)) {
      if (observedKeys.has(key)) {
        this.prepareObservedValue(value[key], { key });
        continue;
      }

      observedKeys.add(key);
      let currentValue = this.prepareObservedValue(value[key], { key });

      Object.defineProperty(value, key, {
        configurable: true,
        enumerable: true,
        get: () => currentValue,
        set: (nextValue: unknown) => {
          if (Object.is(currentValue, nextValue)) {
            return;
          }

          currentValue = this.prepareObservedValue(nextValue, { key });
          this.markChanged();
        },
      });
    }

    return value;
  }

  private syncChildRoots() {
    for (const key of this.observableKeys) {
      this.prepareObservedValue(
        (this as unknown as Record<string, unknown>)[key],
        { key }
      );
    }
  }

  private syncChildDebugPaths() {
    for (const key of this.observableKeys) {
      this.prepareObservedValue(
        (this as unknown as Record<string, unknown>)[key],
        { key }
      );
    }
  }
}

export class ObservableModelRoot extends ObservableModelNode {
  private listeners = new Set<ChangeListener>();
  private revision = 0;

  constructor(debugPathRoot: string | null = null) {
    super();
    this.setObserverRoot(this);
    this.setDebugPathRoot(debugPathRoot);
  }

  subscribe(listener: ChangeListener) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  getRevision() {
    return this.revision;
  }

  markChanged() {
    this.revision += 1;
    this.listeners.forEach((listener) => listener());
  }
}
